import { CodeEnum, type PermissionsLocalized } from 'src/api/main'
import { workspaceSideNavKeyFromPath } from 'src/router/workspaceSideNav'

/**
 * Workspace permission codes, straight from the backend enum
 * (`GET /organizations/workspace-permissions/`). Re-exported under a
 * self-describing name because `CodeEnum` says nothing at a call site.
 */
export const WorkspacePermissionCode = CodeEnum
export type WorkspacePermissionCode = CodeEnum

/**
 * Single permission check against the effective permission list from
 * `GET /organizations/permissions/user/`. Matching is by `code`, so the list
 * may carry app-scoped permissions too without disturbing workspace checks.
 *
 * An absent list means "not resolved yet" and denies — callers that need to
 * distinguish loading from denied read `isLoading` off the context.
 */
export const hasWorkspacePermission = (
  permissions: PermissionsLocalized[] | undefined,
  code: WorkspacePermissionCode
): boolean => {
  if (!permissions || permissions.length === 0 || !code) {
    return false
  }

  return permissions.some((permission) => permission.code === code)
}

/**
 * Sidebar menu key → the permission codes that open it (OR semantics: any one
 * is enough). Derived from the backend's own "what this code opens" table.
 *
 * Keys mirror `WORKSPACE_MENU_KEYS` in `workspaceMenuAccess.ts`.
 */
export const WORKSPACE_MENU_PERMISSIONS = {
  home: [WorkspacePermissionCode.ORGANIZATION_VIEW],
  overview: [WorkspacePermissionCode.ORGANIZATION_VIEW],
  // Groups are a tab of the Users section, so either code opens it — the tab
  // strip then decides which of the two tabs the member actually gets.
  users: [
    WorkspacePermissionCode.USERS_VIEW,
    WorkspacePermissionCode.GROUPS_VIEW
  ],
  // The organization page is the organigram: teams and the people in them.
  organization: [WorkspacePermissionCode.USERS_VIEW],
  companyGraph: [WorkspacePermissionCode.USERS_VIEW],
  roles: [WorkspacePermissionCode.ROLES_VIEW],
  security: [WorkspacePermissionCode.ORGANIZATION_MANAGE],
  workspaceSettings: [WorkspacePermissionCode.ORGANIZATION_MANAGE],
  domains: [WorkspacePermissionCode.ORGANIZATION_MANAGE],
  publicAccess: [WorkspacePermissionCode.ORGANIZATION_MANAGE],
  // Every member connects their own AI client; the page hides admin tabs.
  aiConnectors: [WorkspacePermissionCode.ORGANIZATION_VIEW],
  billing: [WorkspacePermissionCode.ORGANIZATION_MANAGE],
  apps: [WorkspacePermissionCode.APPLICATIONS_VIEW],
  // Agents have no code of their own in the catalog; they are provisioned
  // alongside applications, so they follow `applications.view`.
  agents: [WorkspacePermissionCode.APPLICATIONS_VIEW],
  audit: [WorkspacePermissionCode.AUDIT_VIEW]
} as const satisfies Record<string, readonly WorkspacePermissionCode[]>

export type WorkspaceMenuKey = keyof typeof WORKSPACE_MENU_PERMISSIONS

/** True when `permissions` satisfies any of `codes` (OR). */
export const hasSomeWorkspacePermission = (
  permissions: PermissionsLocalized[] | undefined,
  codes: readonly WorkspacePermissionCode[]
): boolean => codes.some((code) => hasWorkspacePermission(permissions, code))

/** True when `permissions` satisfies every one of `codes` (AND). */
export const hasEveryWorkspacePermission = (
  permissions: PermissionsLocalized[] | undefined,
  codes: readonly WorkspacePermissionCode[]
): boolean => codes.every((code) => hasWorkspacePermission(permissions, code))

/**
 * Menu keys the effective permission list opens. Keys absent from
 * `WORKSPACE_MENU_PERMISSIONS` are not gated and are always allowed.
 */
export const getAllowedWorkspaceMenuKeys = (
  permissions: PermissionsLocalized[] | undefined
): ReadonlySet<string> => {
  const allowed = new Set<string>()
  for (const [key, codes] of Object.entries(WORKSPACE_MENU_PERMISSIONS)) {
    if (hasSomeWorkspacePermission(permissions, codes)) {
      allowed.add(key)
    }
  }

  return allowed
}

/**
 * Deep-link guard aligned with the sidebar keys from
 * `workspaceSideNavKeyFromPath`. A path whose key carries no entry in
 * `WORKSPACE_MENU_PERMISSIONS` is not gated and stays reachable.
 */
export const isWorkspacePathAllowedByPermissions = (
  pathname: string,
  permissions: PermissionsLocalized[] | undefined
): boolean => {
  const navKey = workspaceSideNavKeyFromPath(pathname)
  if (navKey === 'workspaces') {
    return true
  }
  const codes = (
    WORKSPACE_MENU_PERMISSIONS as Record<
      string,
      readonly WorkspacePermissionCode[] | undefined
    >
  )[navKey]

  return codes ? hasSomeWorkspacePermission(permissions, codes) : true
}

/** Installing, configuring and removing workspace applications. */
export const canManageWorkspaceApplications = (
  permissions: PermissionsLocalized[] | undefined
): boolean =>
  hasWorkspacePermission(
    permissions,
    WorkspacePermissionCode.APPLICATIONS_MANAGE
  )
